import { useMemo, useState } from 'react';
import { Head } from '../../common/utils/Head';
import { useTranslation } from 'react-i18next';
import { SubPageModel } from '../../common/model/ui/sub-page.model';
import PageContentTemplate from '../templates/PageContentTemplate';
import GroupsIcon from '@mui/icons-material/Groups';
import SettingsIcon from '@mui/icons-material/Settings';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import ArticleIcon from '@mui/icons-material/Article';
import PollIcon from '@mui/icons-material/Poll';
import UserDetails from '../templates/UserDetails';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import OvPageHeader from '../UI/organisms/OvPageHeader';
import AdminClinicList from '../templates/AdminClinicList';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ClinicDetails from '../templates/ClinicDetails';
import AdminLotList from '../templates/AdminLotList';
import LotDetails from '../templates/LotDetails';
import OvIdleTimer from '../UI/organisms/OvIdleTimer';
import ClinicLocationDetails from '../templates/ClinicLocationDetails';
import { useAppSelector } from '../../redux/hooks';
import { ClinicLocation } from '../../common/model/dto/clinic-location';
import useAuthorized from '../../hooks/use-authorized';
import { Resource } from '../../common/model/type/resource.enum';
import { Role } from '../../common/model/type/role.enum';
import FilterablePatientList from '../templates/FilterablePatientList';
import PatientDetails from '../templates/PatientDetails';
import SelectedClinicInfo from '../templates/SelectedClinicInfo';
import OvPatientOwnResults from '../UI/organisms/OvPatientOwnResults';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import OvProfileUserInfo from '../UI/organisms/OvProfileUserInfo';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import OrderDetails from '../templates/OrderDetails';
import DailyDataDetails from '../templates/DailyDataDetails';
import ScanDetails from '../templates/ScanDetails';
import PatientDailyDataDetails from '../templates/PatientDailyDataDetails';
import AdminScanList from '../templates/AdminScanList';
import Alert from '@mui/material/Alert';
import styled from 'styled-components';
import { AlertTitle } from '@mui/material';
import Variables from '../../design-system/variables';
import Colours from '../../design-system/colours';
import OrderForm from '../templates/OrderForm';
import ClinicLocationScanList from '../templates/ClinicLocationScanList';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AdminScanSequencesList from '../templates/AdminScanSequencesList';
import ScanSequenceDetails from '../templates/ScanSequenceDetails';
import OvShopDialog from '../UI/molecules/OvShopDialog';
import { UserInfo } from '../../common/model/dto/user-info';
import ArticleForm from '../templates/ArticleForm';
import AdminArticleList from '../templates/AdminArticleList';
import ArticleDetails from '../templates/ArticleDetails';
import AdminApiKeyList from '../templates/AdminApiKeyList';
import ApiKeyCreateEditPage from '../templates/ApiKeyCreateEditPage';
import ApiKeyDetails from '../templates/ApiKeyDetails';
import KeyIcon from '@mui/icons-material/Key';
import Configuration from '../templates/Configuration';
import AdminEntitlementsList from '../templates/AdminEntitlementsList';
import EntitlementIcon from '@mui/icons-material/Lock';
import { isEmpty } from 'lodash';
import { hasManagerRole, hasViewerRole } from '../../common/utils/auth.helpers';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationContentDetails from '../templates/NotificationContentDetails';
import AdminUserList from '../templates/AdminUserList';
import AdminOrdersList from '../templates/AdminOrdersList';
import AdminNotificationList from '../templates/AdminNotificationList';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import NotificationDetails from '../templates/NotificationDetails';
import Surveys from '../templates/Surveys';
import OvProminentActions from '../UI/molecules/OvProminentActions';
import NpsSurveyBuilder from '../templates/NpsSurveyBuilder';
import NotificationsContents from '../templates/NotificationsContents';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AdminPhysicalProductList from '../templates/AdminPhysicalProductList';
import PhysicalProductDetails from '../templates/PhysicalProductDetails';
import AdminShippingRateList from '../templates/AdminShippingRateList';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShippingRateDetails from '../templates/ShippingRateDetails';
import HormonePatternContentDetails from '../templates/HormonePatternContentDetails';
import StrawStageContentDetails from '../templates/StrawStageContentDetails';
import CurrentCycleContentDetails from '../templates/CurrentCycleContentDetails';
import UserCycleDetails from '../templates/UserCycleDetails';
import { PATIENT } from '../../common/model/type/clinic-role';
import { getAuthenticatedLandingPath } from '../../common/route-guards/authenticated-landing-path';
import { VideoUploadProvider } from '../../context/VideoUploadContext';
import OvVideoStagingProvider from '../UI/organisms/OvVideoStagingProvider';
import AdminVideoList from '../templates/AdminVideoList';
import VideoDetails from '../templates/VideoDetails';
import VideoUpload from '../templates/VideoUpload';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { useVideoPermissions } from '../../hooks/use-video-permissions';

const ApplicationBasePage = () => {
  const { t } = useTranslation();
  const entitlements = useAppSelector((state) => state.auth.entitlements);

  const selectedRole = useAppSelector((state) => state.user.selectedRole);
  const currentUser: UserInfo | undefined = useAppSelector(
    (state) => state.user.currentUser
  );
  const selectedClinicLocation: ClinicLocation | undefined = useAppSelector(
    (state) => state.user.selectedClinicLocation
  );
  const selectedClinicRole: string | undefined = useAppSelector(
    (state) => state.user.selectedClinicRole
  );
  const allowedPatientOperations = useAuthorized(
    Resource.ClinicPatient,
    selectedClinicLocation?.id,
    selectedClinicRole
  );
  const allowedClinicLocationOperations = useAuthorized(
    Resource.ClinicLocation,
    selectedClinicLocation?.id,
    selectedClinicRole
  );
  const canReadPatients = allowedPatientOperations.read;
  const canReadClinicLocation = allowedClinicLocationOperations.read;
  const isPatientContext = selectedClinicRole === PATIENT;

  const [isShopDialogOpen, setIsShopDialogOpen] = useState<boolean>(false);

  const userOperations = useAuthorized(Resource.User);
  const scanOperations = useAuthorized(Resource.Scan);
  const dailyDataOperations = useAuthorized(Resource.DailyData);
  const orderOperations = useAuthorized(Resource.Order);
  const lotOperations = useAuthorized(Resource.Lot);
  const clinicOperations = useAuthorized(Resource.Clinic);
  const articleOperations = useAuthorized(Resource.Article);
  const apiKeyOperations = useAuthorized(Resource.ApiKey);
  const scanningPlanOperations = useAuthorized(Resource.ScanningPlan);
  const { canRead: canReadVideos } = useVideoPermissions();

  const subPages = useMemo<SubPageModel[]>(() => {
    if (selectedRole === Role.Admin) {
      return [
        {
          url: 'configuration-settings',
          component: <Configuration />,
          linkLabel: t('app.subPages.menu.configurationSettings'),
          linkIcon: <SettingsIcon />,
          isVisible: true,
        },
        {
          url: 'current-cycle-contents/:currentCycleContentId',
          component: <CurrentCycleContentDetails />,
          isVisible: true,
        },
        {
          url: 'straw-stage-contents/:strawStageContentId',
          component: <StrawStageContentDetails />,
          isVisible: true,
        },
        {
          url: 'hormone-pattern-contents/:hormonePatternContentId',
          component: <HormonePatternContentDetails />,
          isVisible: true,
        },
        {
          url: 'user-cycles/:userCycleId',
          component: <UserCycleDetails />,
          isVisible: true,
        },
        {
          url: 'notifications/:notificationId',
          component: <NotificationDetails />,
          isVisible: true,
        },
        {
          url: 'notifications',
          component: <AdminNotificationList />,
          linkLabel: t('app.subPages.menu.notifications'),
          linkIcon: <NotificationsIcon />,
          isVisible: true,
        },
        {
          url: 'notification-contents/:category/:notificationContentId',
          component: <NotificationContentDetails />,
          isVisible: true,
        },
        {
          url: 'notification-contents',
          component: <NotificationsContents />,
          linkLabel: t('app.subPages.menu.notificationContents'),
          linkIcon: <MarkUnreadChatAltIcon />,
          isVisible: true,
        },
        {
          url: 'videos/upload',
          component: <VideoUpload />,
          isVisible: true,
        },
        {
          url: 'videos/:videoId',
          component: <VideoDetails />,
          isVisible: true,
        },
        {
          url: 'videos',
          component: <AdminVideoList />,
          linkLabel: t('app.subPages.menu.videos'),
          linkIcon: <VideoLibraryIcon />,
          isVisible: true,
        },
        {
          url: 'entitlements',
          component: <AdminEntitlementsList />,
          linkLabel: t('common.entitlements'),
          linkIcon: <EntitlementIcon />,
          isVisible: true,
        },
        {
          url: 'scan-sequences/:scanSequenceId',
          component: <ScanSequenceDetails />,
          isVisible: true,
        },
        {
          url: 'scan-sequences',
          component: <AdminScanSequencesList />,
          linkLabel: t('app.subPages.menu.scanSequences'),
          linkIcon: <ChecklistIcon />,
          isVisible: true,
        },
        {
          url: 'clinics/:clinicId/clinic-info/:clinicLocationId',
          component: <ClinicLocationDetails />,
          isVisible: true,
        },
        {
          url: 'clinics/:clinicId',
          component: <ClinicDetails />,
          isVisible: true,
        },
        {
          url: 'surveys/nps/builder',
          component: <NpsSurveyBuilder />,
          isVisible: true,
        },
        {
          url: 'surveys',
          component: <Surveys />,
          linkLabel: t('common.surveys'),
          linkIcon: <PollIcon />,
          isVisible: true,
        },
        {
          url: 'articles/create',
          component: <ArticleForm />,
          isVisible: true,
        },
        {
          url: 'articles/edit/:articleId',
          component: <ArticleForm />,
          isVisible: true,
        },
        {
          url: 'articles/detail/:articleId',
          component: <ArticleDetails />,
          isVisible: true,
        },
        {
          url: 'articles',
          component: <AdminArticleList />,
          linkLabel: t('common.articles'),
          linkIcon: <ArticleIcon />,
          isVisible: true,
        },
        {
          url: 'api-keys/create',
          component: <ApiKeyCreateEditPage />,
          isVisible: true,
        },
        {
          url: 'api-keys/edit/:apiKeyId',
          component: <ApiKeyCreateEditPage />,
          isVisible: true,
        },
        {
          url: 'api-keys/detail/:apiKeyId',
          component: <ApiKeyDetails />,
          isVisible: true,
        },
        {
          url: 'api-keys',
          component: <AdminApiKeyList />,
          linkLabel: t('common.apiKeys'),
          linkIcon: <KeyIcon />,
          isVisible: true,
        },

        {
          url: 'shipping-rates/:shippingRateId',
          component: <ShippingRateDetails />,
          isVisible: true,
        },
        {
          url: 'shipping-rates',
          component: <AdminShippingRateList />,
          linkLabel: t('app.subPages.menu.shippingRates'),
          linkIcon: <LocalShippingIcon />,
          isVisible: true,
        },
        {
          url: 'physical-products/:physicalProductId',
          component: <PhysicalProductDetails />,
          isVisible: true,
        },
        {
          url: 'physical-products',
          component: <AdminPhysicalProductList />,
          linkLabel: `${t('app.subPages.menu.physicalProducts')} (new)`,
          linkIcon: <StorefrontIcon />,
          isVisible: true,
        },
        {
          url: 'clinics',
          component: <AdminClinicList />,
          linkLabel: t('app.subPages.menu.clinics'),
          linkIcon: <LocalHospitalIcon />,
          isVisible: true,
        },
        {
          url: 'lots/:lotId',
          component: <LotDetails />,
          isVisible: true,
        },
        {
          url: 'lots',
          component: <AdminLotList />,
          linkLabel: t('app.subPages.menu.lots'),
          linkIcon: <QrCodeIcon />,
          isVisible: true,
        },
        {
          url: 'orders/create',
          component: <OrderForm />,
          isVisible: true,
        },
        {
          url: 'orders/:orderId',
          component: <OrderDetails />,
          isVisible: true,
        },
        {
          url: 'orders',
          component: <AdminOrdersList />,
          linkLabel: t('app.subPages.menu.orders'),
          linkIcon: <ShoppingCartIcon />,
          isVisible: true,
        },
        {
          url: 'daily-data/:dailyDataId/scans/:scanId',
          component: <ScanDetails />,
          isVisible: true,
        },
        {
          url: 'daily-data/:dailyDataId',
          component: <DailyDataDetails />,
          isVisible: true,
        },
        {
          url: 'users/:userId',
          component: <UserDetails />,
          isVisible: true,
        },
        {
          url: 'scan-list/:scanId',
          component: <ScanDetails />,
          isVisible: true,
        },
        {
          url: 'scan-list',
          component: <AdminScanList />,
          linkLabel: t('app.subPages.menu.scans'),
          linkIcon: <DocumentScannerIcon />,
          isVisible: true,
        },
        {
          url: 'users',
          component: <AdminUserList />,
          linkLabel: t('app.subPages.menu.users'),
          linkIcon: <GroupsIcon />,
          isVisible: true,
        },
        {
          url: '',
          redirectUrl: 'users',
          component: <AdminUserList />,
          isVisible: true,
        },
      ];
    }

    if (
      selectedRole === Role.Regular &&
      selectedClinicLocation &&
      selectedClinicRole
    ) {
      return [
        {
          url: 'https://buy.stripe.com/eVaeYV5IYfkB6B24gv',
          isExternalPage: true,
          linkIcon: <ShoppingBagIcon />,
          linkLabel: t('app.subPages.menu.shop'),
          isVisible: true,
          shouldOpenDialog: true,
          handleDialogOpen: () => setIsShopDialogOpen(true),
        },
        {
          url: 'profile',
          component: <OvProfileUserInfo />,
          linkLabel: t('app.subPages.menu.profile'),
          linkIcon: <AccountCircleRoundedIcon />,
          isVisible: canReadPatients,
        },
        {
          url: 'scan-list',
          component: <ClinicLocationScanList />,
          linkLabel: t('app.subPages.menu.scans'),
          linkIcon: <DocumentScannerIcon />,
          isVisible: true,
        },
        {
          url: 'clinic-info',
          component: <SelectedClinicInfo />,
          linkLabel: t('app.subPages.menu.clinicSiteInfo'),
          linkIcon: <LocalHospitalIcon />,
          isVisible: canReadClinicLocation,
        },
        {
          url: 'patients/:patientId',
          component: <PatientDetails />,
          isVisible: canReadPatients,
        },
        {
          url: 'results',
          component: isPatientContext ? <OvPatientOwnResults /> : undefined,
          redirectUrl: isPatientContext ? undefined : 'patients',
          linkLabel: isPatientContext
            ? t('app.subPages.menu.results')
            : undefined,
          linkIcon: isPatientContext ? <QueryStatsIcon /> : undefined,
          isVisible: true,
        },
        {
          url: 'patients',
          component: <FilterablePatientList />,
          linkLabel: t('app.subPages.menu.patients'),
          linkIcon: <GroupsIcon />,
          isVisible: canReadPatients,
        },
        {
          url: 'daily-data/:dailyDataId',
          component: <PatientDailyDataDetails />,
          isVisible: true,
        },
        {
          url: '',
          component: isPatientContext ? <OvPatientOwnResults /> : undefined,
          redirectUrl: getAuthenticatedLandingPath(
            selectedRole,
            selectedClinicRole
          ).slice(1),
          isVisible: true,
        },
      ];
    }

    if (
      (hasManagerRole([selectedRole]) || hasViewerRole([selectedRole])) &&
      ![Role.Admin, Role.Regular].includes(selectedRole)
    ) {
      const subPageConfigs = [
        {
          url: 'videos/upload',
          component: <VideoUpload />,
          isVisible: canReadVideos,
        },
        {
          url: 'videos/:videoId',
          component: <VideoDetails />,
          isVisible: canReadVideos,
        },
        {
          url: 'videos',
          component: <AdminVideoList />,
          linkLabel: t('app.subPages.menu.videos'),
          linkIcon: <VideoLibraryIcon />,
          isVisible: canReadVideos,
        },
        {
          url: 'user-cycles/:userCycleId',
          component: <UserCycleDetails />,
          isVisible: true,
        },
        {
          url: 'scan-sequences/:scanSequenceId',
          component: <ScanSequenceDetails />,
          isVisible: scanningPlanOperations.supervise,
        },
        {
          url: 'scan-sequences',
          component: <AdminScanSequencesList />,
          linkLabel: t('app.subPages.menu.scanSequences'),
          linkIcon: <ChecklistIcon />,
          isVisible: scanningPlanOperations.supervise,
        },
        {
          url: 'clinics/:clinicId/clinic-info/:clinicLocationId',
          component: <ClinicLocationDetails />,
          isVisible: clinicOperations.supervise,
        },
        {
          url: 'clinics/:clinicId',
          component: <ClinicDetails />,
          isVisible: clinicOperations.supervise,
        },
        {
          url: 'articles/create',
          component: <ArticleForm />,
          isVisible: articleOperations.create,
        },
        {
          url: 'articles/edit/:articleId',
          component: <ArticleForm />,
          isVisible: articleOperations.update,
        },
        {
          url: 'articles/detail/:articleId',
          component: <ArticleDetails />,
          isVisible: articleOperations.supervise,
        },
        {
          url: 'articles',
          component: <AdminArticleList />,
          linkLabel: t('common.articles'),
          linkIcon: <ArticleIcon />,
          isVisible: articleOperations.supervise,
        },
        {
          url: 'api-keys/create',
          component: <ApiKeyCreateEditPage />,
          isVisible: apiKeyOperations.create,
        },
        {
          url: 'api-keys/edit/:apiKeyId',
          component: <ApiKeyCreateEditPage />,
          isVisible: apiKeyOperations.update,
        },
        {
          url: 'api-keys/detail/:apiKeyId',
          component: <ApiKeyDetails />,
          isVisible: apiKeyOperations.supervise,
        },
        {
          url: 'api-keys',
          component: <AdminApiKeyList />,
          linkLabel: t('common.apiKeys'),
          linkIcon: <KeyIcon />,
          isVisible: apiKeyOperations.supervise,
        },

        {
          url: 'clinics',
          component: <AdminClinicList />,
          linkLabel: t('app.subPages.menu.clinics'),
          linkIcon: <LocalHospitalIcon />,
          isVisible: clinicOperations.supervise,
        },
        {
          url: 'lots/:lotId',
          component: <LotDetails />,
          isVisible: lotOperations.supervise,
        },
        {
          url: 'lots',
          component: <AdminLotList />,
          linkLabel: t('app.subPages.menu.lots'),
          linkIcon: <QrCodeIcon />,
          isVisible: lotOperations.supervise,
        },
        {
          url: 'orders/create',
          component: <OrderForm />,
          isVisible: orderOperations.create,
        },
        {
          url: 'orders/:orderId',
          component: <OrderDetails />,
          isVisible: orderOperations.supervise,
        },
        {
          url: 'orders',
          component: <AdminOrdersList />,
          linkLabel: t('app.subPages.menu.orders'),
          linkIcon: <ShoppingCartIcon />,
          isVisible: orderOperations.supervise,
        },
        {
          url: 'daily-data/:dailyDataId/scans/:scanId',
          component: <ScanDetails />,
          isVisible: scanOperations.read,
        },
        {
          url: 'daily-data/:dailyDataId',
          component: <DailyDataDetails />,
          isVisible: dailyDataOperations.read,
        },
        {
          url: 'users/:userId',
          component: <UserDetails />,
          isVisible: userOperations.supervise,
        },
        {
          url: 'users',
          component: <AdminUserList />,
          linkLabel: t('app.subPages.menu.users'),
          linkIcon: <GroupsIcon />,
          isVisible: userOperations.supervise,
        },
        {
          url: 'scan-list/:scanId',
          component: <ScanDetails />,
          isVisible: scanOperations.supervise,
        },
        {
          url: 'scan-list',
          component: <AdminScanList />,
          linkLabel: t('app.subPages.menu.scans'),
          linkIcon: <DocumentScannerIcon />,
          isVisible: scanOperations.supervise,
        },
      ];

      const redirectUrl = [...subPageConfigs]
        .filter((config) => !isEmpty(config.url))
        .reverse()
        .find((config) => config.isVisible && !config.url.includes(':'))?.url;

      return [
        ...subPageConfigs,
        {
          url: '',
          redirectUrl,
          isVisible: true,
        },
      ];
    }

    return [];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canReadPatients,
    canReadClinicLocation,
    canReadVideos,
    isPatientContext,
    selectedRole,
    selectedClinicRole,
    entitlements,
  ]);

  return (
    <VideoUploadProvider>
      <OvVideoStagingProvider>
        <Head title={t('app.pageTitle')} description={t('login.pageDesc')} />
        <OvPageHeader />
        {process.env.REACT_APP_ENV === 'staging' && (
          <StyledAlert severity="warning">
            <AlertTitle>{t('app.staging.alertTitle')}</AlertTitle>
            {t('app.staging.alertDesc')}
          </StyledAlert>
        )}
        <OvIdleTimer />
        <PageContentTemplate subPages={subPages} />
        <OvShopDialog
          isOpen={isShopDialogOpen}
          onClose={() => setIsShopDialogOpen(false)}
          currentUser={currentUser}
          selectedClinicLocation={selectedClinicLocation}
        />
        {currentUser && (
          <OvProminentActions
            selectedRole={selectedRole}
            userId={currentUser.id}
          />
        )}
      </OvVideoStagingProvider>
    </VideoUploadProvider>
  );
};

export default ApplicationBasePage;

const StyledAlert = styled(Alert)`
  && {
    position: relative;
    z-index: 1;
    top: 4.25rem;
    margin: 0.25rem 0.5rem 0 0.5rem;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_YELLOW};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
